
import { GiftshopEditModel, GiftshopPricegroup, GiftshopProduct } from '@/models/Giftshop';
import { Component, Vue, Prop } from 'vue-property-decorator';
import GiftshopSelectionModalProduct from './GiftshopSelectionModalProduct.vue';
import GiftshopSelectionProduct from './GiftshopSelectionProduct.vue';
import GiftshopService from '@/services/GiftshopService';
import { copy } from '@/utils/objectUtils';

const service: GiftshopService = new GiftshopService();

@Component({
    name: 'GiftshopPricegroups',
    components: {
        GiftshopSelectionProduct,
        GiftshopSelectionModalProduct,
    }
})
export default class GiftshopPricegroups extends Vue {
    @Prop({ type: String, required: true }) buttonText: string;
    @Prop({ type: String, required: true }) contactImage: string;
    @Prop({ type: String, required: true }) contactUrl: string;
    @Prop({ type: Array, required: true }) pricegroups: GiftshopPricegroup[];

    model: GiftshopEditModel = {
        name: "Ny gaveshop",
        pricegroups: [],
        products: [],
        company: null,
        contactname: null,
        contactphone: null,
        contactemail: null,
        wrapping: false,
    };
    step: number = 1;
    modalProduct: GiftshopProduct = null;
    loading: boolean = false;

    get steps(): any {
        return [
            {
                label: this.$t('giftshop.pricegroup-step1', ['Vælg udvalg']),
                class: this.step == 1 ? 'active' : this.step > 1 ? 'completed' : null,
            },
            {
                label: this.$t('giftshop.pricegroup-step2', ['Informationer']),
                class: this.step == 2 ? 'active' : this.step > 2 ? 'completed' : null,
            },
            {
                label: this.$t('giftshop.pricegroup-step3', ['Afslut']),
                class: this.step == 3 ? 'active' : this.step > 3 ? 'completed' : null,
            },
        ]
    }

    get selectedPricegroups(): GiftshopPricegroup[] {
        return this.pricegroups.filter(o => this.model.pricegroups.includes(o.id));
    }

    get products(): GiftshopProduct[] {
        return this.selectedPricegroups.reduce((input, o) => {
            return [
                ...input,
                ...o.products,
            ];
        }, []);
    }

    get selectedProducts(): GiftshopProduct[] {
        return this.products.filter(prod => this.model.products.includes(prod.id));
    }

    get selectedPricegroupsFiltered(): GiftshopPricegroup[] {
        return copy(this.selectedPricegroups).map((o: GiftshopPricegroup) => {
            o.products = o.products.filter((prod: GiftshopProduct) => !this.model.products.includes(prod.id));
            return o;
        }).filter((o: GiftshopPricegroup) => o.products.length > 0);
    }

    get showModal(): boolean {
        return this.modalProduct != null;
    }

    get fields(): any[] {
        return [
            // {
            //     type: "header",
            //     label: this.$t('giftshop.giftshop-information', ['Din gaveshop']),
            // },
            // {
            //     id: "name",
            //     type: "text",
            //     label: this.$t('giftshop.name-your-giftshop', ['Navngiv din gaveshop']),
            //     required: true,
            // },
            {
                type: "header",
                label: this.$t('giftshop.contact-information', ['Kontaktinformationer']),
            },            
            {
                id: "company",
                type: "text",
                label: this.$t('giftshop.company', ['Virksomhed']),
                required: true,
            },
            {
                id: "contactname",
                type: "text",
                label: this.$t('giftshop.contact-person', ['Kontaktperson']),
                required: true,
            },
            {
                id: "contactphone",
                type: "tel",
                label: this.$t('giftshop.phone', ['Telefon']),
                required: true,
            },
            {
                id: "contactemail",
                type: "email",
                label: this.$t('giftshop.email', ['E-mail']),
                required: true,
            },
{
                id: "wrapping",
                type: "toggle",
                label: this.$t('giftshop.gift-wrapping', ['Jeg ønsker indpakning (25,- pr. gave)']),
                required: false,
            },
        ];
    }

    goToContactUrl() {
        location.href = this.contactUrl;
    }

    prevStep(scrollIntoView: boolean = false) {
        if (this.step == 1) {
            this.model.pricegroups = [];
            this.model.products = [];
        }
        else this.step--;
        
        if (scrollIntoView)
            this.scrollIntoView();
    }

    nextStep(scrollIntoView: boolean = false) {
        this.step++;

        if (scrollIntoView)
            this.scrollIntoView();
    }

    private scrollIntoView() {
        const headerHeight = document.querySelector('.cbx-navbar-sticky').clientHeight;
        const offsetTop = (document.querySelector('.giftshop-pricegroups').parentElement.closest(".uk-container") as HTMLElement).offsetTop;
        const gutter = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--global-gutter'));
        window.scrollTo(0, offsetTop - headerHeight - gutter);
    }

    isSelected(pricegroup: string) {
        return this.model.pricegroups.includes(pricegroup);
    }

    toggleSelection(pricegroup: string) {
        if (this.isSelected(pricegroup)) {
            this.model.pricegroups = this.model.pricegroups.filter((o: string) => o !== pricegroup);
            this.model.products = this.model.products.filter((o: string) => this.selectedProducts.map(p => p.id).includes(o));
        }
        else
        {
            this.model.pricegroups.push(pricegroup);
            if (this.model.products.length == 0)
                this.model.products = this.products.filter(o => o.featured).map(o => o.id);
        }
    }

    viewProduct(prod: GiftshopProduct) {
        this.modalProduct = prod;
    }

    isProductSelected(product: string) {
        return this.model.products.includes(product);
    }

    toggleProduct(product: string) {
        if (this.isProductSelected(product))
            this.model.products = this.model.products.filter((o: string) => o !== product);
        else
            this.model.products.push(product);
    }

    modalHide() {
        this.modalProduct = null;
    }

    getRules(field: any) {
        const rules = [];

        if (field.required)
            rules.push('required');

        if (field.type === 'email')
            rules.push('email');

        return rules.join('|');
    }

    async finalize() {
        this.loading = true;

        if (await service.addGiftshop(this.model))
            this.nextStep(true);

        this.loading = false;
    }
}
